/**
 * Os TEXTOS que o relatório comparativo (lote de 09/10/2026, item 7) divide com o simulador
 * legado — puro, sem DOM (roda no vitest em Node).
 *
 * POR QUE UMA CÓPIA, E POR QUE ELA NÃO PODE DIVERGIR
 * Os objetivos do modo Guiado (`GUIA_OBJ`), os nomes dos 7 critérios (`CRIT_NOMES`), a lista
 * de campos obrigatórios (`faltantes`), as alavancas do diagnóstico (`guiaDiagView`) e a folga
 * do critério mais apertado (`guiaFolgas`) moram em `simulador.js`, que é script clássico e
 * não se importa. O relatório precisa deles em TypeScript. Duas fontes para o mesmo texto é
 * como o relatório passaria a dizer uma coisa e a tela outra — por isso
 * `tests/relatorio-paridade.test.ts` lê o PRÓPRIO `simulador.js`, avalia esses trechos
 * isolados e exige que sejam iguais a estes. Mudou lá, o teste reprova aqui.
 *
 * Vocabulário: o dos critérios da tela (EL-129/EL-167) — "Vendidas", "Financiadas", "LGC
 * Disponível". A tabela da Comparação ainda diz "Comercializadas/Repassadas" (herança da
 * v67); o relatório segue os critérios, que são o que a pessoa lê no veredito.
 *
 * Nenhum texto daqui cita ID (EL-xxx) — regra da casa, conferida no teste.
 */
import type { Empreendimento, Lev } from '../../../../engine/index';

/* ============================================================================
 * Objetivos (os mesmos do modo Guiado — `GUIA_OBJ`, simulador.js)
 * ========================================================================== */

export type ChaveObjetivo = 'recProprios' | 'lgc' | 'resultado' | 'folga' | 'comercial' | 'alavancagem';

export interface Objetivo {
  k: ChaveObjetivo;
  /** título (igual ao cartão do Guiado) */
  t: string;
  /** descrição (igual ao cartão do Guiado) */
  d: string;
}

export const OBJETIVOS: readonly Objetivo[] = [
  { k: 'recProprios', t: 'Comprometer o mínimo de recursos próprios', d: 'Aporte + terreno em garantia — liberar capital para outros projetos.' },
  { k: 'lgc', t: 'Preservar minha linha de crédito (LGC)', d: 'Consumir o mínimo de LGC aqui para tomar outros projetos.' },
  { k: 'resultado', t: 'Maximizar o resultado absoluto', d: 'Maior lucro em reais (VGV − custo total do empreendimento).' },
  { k: 'folga', t: 'Máxima folga / robustez', d: 'Não ficar no fio da navalha em nenhum critério.' },
  { k: 'comercial', t: 'Facilitar o desafio comercial', d: 'Exigir o mínimo de unidades vendidas/financiadas para viabilizar.' },
  { k: 'alavancagem', t: 'Máxima alavancagem', d: 'Mais PJ por real próprio (proxy do retorno — cuidado: mais PJ = mais LGC/juros).' },
];

export function objetivoPorChave(k: string | null | undefined): Objetivo | null {
  return OBJETIVOS.find((o) => o.k === k) ?? null;
}

/* ============================================================================
 * Os 7 critérios (`CRIT_NOMES`, simulador.js) — mesma ordem do traço do veredito
 * ========================================================================== */

export const CRIT_NOMES: readonly string[] = [
  'Garantias ≥ CTO', 'Vendidas ≥ mín', 'Financiadas ≥ mín', 'LGC Disponível', 'IGH ≥ mínimo', 'Obra ≥ mínimo', 'Data ≤ normativo',
];

/* ============================================================================
 * Campos obrigatórios (`faltantes`, simulador.js) — só os RÓTULOS
 *
 * A tela usa a mesma regra para recusar o "Salvar" e para pintar "Dados insuficientes para
 * gerar o cenário". Um cenário publicado pode ainda assim chegar aqui incompleto (um cenário
 * novo nasce EM BRANCO desde a 0032, e versões antigas podem faltar campo) — no relatório ele
 * entra marcado "dados insuficientes" e fica fora do ranking.
 * ========================================================================== */

export function faltantesDoCenario(b: Empreendimento, L: Lev): string[] {
  const f: string[] = [];
  if (!(b.VGV > 0)) f.push('VGV');
  if (!(b.unidades > 0)) f.push('Unidades');
  if (!(b.ctoLaudo > 0)) f.push('CTO Laudo');
  if (!(b.valorTerreno > 0)) f.push('Valor do terreno');
  if (!(L.unidVendDem > 0)) f.push('Unid. vendidas demanda');
  if (!(L.unidFinDem > 0)) f.push('Unid. financiadas demanda');
  if (!L.dataContratacao) f.push('Data de contratação');
  return f;
}

/* ============================================================================
 * As alavancas do diagnóstico do Guiado (`guiaDiagView`, simulador.js)
 *
 * `k` = a trava do Guiado que esconde a alavanca ("O que você não pode mexer?": aporte,
 * terreno, % financiado, teto de LGC). Sem `k`, a alavanca nunca é escondida.
 * ========================================================================== */

export type ChaveTrava = 'aporte' | 'terreno' | 'pctFin' | 'lgc';

export interface Alavanca {
  t: string;
  k?: ChaveTrava;
}

/** Índice = posição em `CRIT_NOMES`. */
export const ALAVANCAS: readonly (readonly Alavanca[])[] = [
  [
    { t: 'Aumentar o aporte', k: 'aporte' },
    { t: 'Financiar mais unidades na demanda' },
    { t: 'Colocar o terreno em garantia', k: 'terreno' },
    { t: 'Aumentar o % financiado', k: 'pctFin' },
    { t: 'Aumentar a parcela de obra em garantia' },
  ],
  [{ t: 'Vender mais unidades até a demanda' }, { t: 'Ligar EVV (reduz o mínimo)' }],
  [{ t: 'Financiar mais unidades' }, { t: 'Ligar EVV (reduz o mínimo para 1)' }],
  [{ t: 'Reduzir o PJ (cobrir mais com garantias próprias)' }, { t: 'Aumentar o LGC disponível', k: 'lgc' }],
  [{ t: 'Manter mais unidades em estoque (financiar menos)' }, { t: 'Elevar a avaliação das unidades' }],
  [{ t: 'Executar mais obra antes da demanda' }, { t: 'Rever o mínimo de obra' }],
  [{ t: 'Antecipar o cumprimento de demanda' }, { t: 'Rever a data de contratação' }],
];

/** Entra em 1º lugar em "Garantias ≥ CTO", e só quando é o "PJ limite" que corta o PJ. */
export const ALAVANCA_PJ_LIMITE: Alavanca = { t: 'Aumentar ou apagar o PJ limite' };

/* ============================================================================
 * Veredito (as três cores do motor) — as palavras da faixa EL-166 da tela
 * ========================================================================== */

export type Veredito = 'Viável' | 'Viável com ressalvas' | 'Não viável' | 'Dados insuficientes';

export function veredito(cor: 'green' | 'yellow' | 'red' | null): Veredito {
  if (cor === null) return 'Dados insuficientes';
  return cor === 'green' ? 'Viável' : cor === 'yellow' ? 'Viável com ressalvas' : 'Não viável';
}
